// src/hooks/useSubscription.js
// Custom hook for subscription state management

import React, { useState, useEffect, useContext, createContext, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseClient';
import { useAuth } from './useAuth';
import UpgradeModal from '../components/Subscription/UpgradeModal';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null); // e.g., 'limit-reached', 'album-1', 'album-2'

  useEffect(() => {
    if (!user || user.isAnonymous) {
      // Anonymous user = Free tier
      setSubscription({
        tier: 'free',
        status: 'none',
        earlyAdopter: false,
      });
      setLoading(false);
      return;
    }

    // Authenticated user - listen to Firestore
    const userRef = doc(db, 'users', user.uid);

    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSubscription({
            tier: data.subscriptionTier || 'free',
            status: data.subscriptionStatus || 'none',
            earlyAdopter: data.earlyAdopter || false,
            earlyAdopterNumber: data.earlyAdopterNumber || null,
            trialEndsAt: data.trialEndsAt || null,
            billingPeriodEnd: data.billingPeriodEnd || null,
            stripeCustomerId: data.stripeCustomerId || null,
            stripeSubscriptionId: data.stripeSubscriptionId || null,
          });
        } else {
          // User doc does not exist yet
          setSubscription({
            tier: 'free',
            status: 'none',
            earlyAdopter: false,
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('[useSubscription] Error fetching subscription:', error);
        setSubscription({
          tier: 'free',
          status: 'none',
          earlyAdopter: false,
        });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Computed values
  const devProMode =
    typeof window !== 'undefined' && localStorage.getItem('vvd:devProMode') === 'true';

  const { isPro, isTrialing, isEarlyAdopter, isFree } = useMemo(() => {
    const tier = subscription?.tier;
    const status = subscription?.status;

    const proStatuses = new Set(['trialing', 'active']);
    const proByStripe = tier === 'pro' && proStatuses.has(status);

    const computedIsPro = Boolean(devProMode || proByStripe);
    const computedIsTrialing = tier === 'pro' && status === 'trialing';

    return {
      isPro: computedIsPro,
      isTrialing: computedIsTrialing,
      isEarlyAdopter: subscription?.earlyAdopter === true,
      isFree: !computedIsPro,
    };
  }, [subscription, devProMode]);

  const openUpgradeModal = (context = null) => {
    setUpgradeContext(context);
    setShowUpgradeModal(true);
  };

  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
    setUpgradeContext(null);
  };

  // ✅ Important: if the user upgrades successfully (trialing/active),
  // automatically close any upgrade modal that was opened before checkout.
  useEffect(() => {
    if (showUpgradeModal && isPro) {
      closeUpgradeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpgradeModal, isPro]);

  const value = {
    subscription,
    loading,
    isPro,
    isFree,
    isTrialing,
    isEarlyAdopter,
    showUpgradeModal,
    upgradeContext,
    openUpgradeModal,
    closeUpgradeModal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      {/* Render the upgrade modal here - it reads state from context */}
      <UpgradeModal isOpen={showUpgradeModal} onClose={closeUpgradeModal} context={upgradeContext} />
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
}
